import { z } from "zod";
import {
  centsToDecimal,
  decimalToCents,
  decimalToMilli,
  milliToDecimal,
  formatDuration,
  parseDuration,
  RATE_UNIT_LABELS,
} from "@/domain/billing";
import { isDateOnly, toDateOnly } from "@/domain/dates";
import {
  BILLING_CYCLES,
  CURRENCIES,
  PAYMENT_QR_METHODS,
  RATE_UNIT_PRESETS,
  WEEKDAYS,
  type BillingItemType,
  type ClientProfile,
  type CurrencyCode,
  type CompanyProfile,
  type DueRule,
  type InvoiceInput,
  type InvoiceRecord,
  type PartyInfo,
  type PaymentQrAssetMap,
  type RateUnitPreset,
} from "@/domain/types";
import { DEFAULT_CLIENT_NOTE } from "@/features/invoices/client-note-templates";
import { defaultPaymentQrSettings } from "@/features/invoices/payment-qr";
import { createId } from "@/lib/ids";

const addressFormSchema = z.object({
  line1: z.string().trim().max(200),
  line2: z.string().trim().max(200),
  city: z.string().trim().max(100),
  state: z.string().trim().max(100),
  postalCode: z.string().trim().max(30),
  country: z.string().trim().max(100),
});

const partyFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(150),
  company: z.string().trim().max(150),
  email: z
    .string()
    .trim()
    .max(254)
    .refine(
      (value) =>
        value === "" || z.email().safeParse(value).success,
      "Enter a valid email.",
    ),
  phone: z.string().trim().max(50),
  address: addressFormSchema,
});

const companyFormSchema = partyFormSchema.extend({
  tagline: z.string().trim().max(200),
  website: z
    .string()
    .trim()
    .max(500)
    .refine(
      (value) =>
        value === "" || z.url().safeParse(value).success,
      "Enter a valid website URL.",
    ),
});

export const invoiceFormItemSchema = z
  .object({
    id: z.string().min(1),
    description: z
      .string()
      .trim()
      .min(1, "Description is required.")
      .max(500),
    billingType: z.enum(["hourly", "fixed", "quantity"]),
    duration: z.string(),
    hourlyRate: z.number().min(0),
    quantity: z.number().min(0),
    unitPreset: z.enum(RATE_UNIT_PRESETS),
    customUnitLabel: z.string().trim().max(80),
    unitPrice: z.number().min(0),
    fixedAmount: z.number().min(0),
  })
  .superRefine((item, context) => {
    if (item.unitPreset === "custom" && !item.customUnitLabel) {
      context.addIssue({
        code: "custom",
        path: ["customUnitLabel"],
        message: "Enter the agreed price unit.",
      });
    }

    if (item.billingType === "hourly") {
      const seconds = parseDuration(item.duration);

      if (!Number.isFinite(seconds) || seconds <= 0) {
        context.addIssue({
          code: "custom",
          path: ["duration"],
          message:
            "Use HH:MM:SS and enter a duration greater than zero.",
        });
      }

      if (item.hourlyRate <= 0) {
        context.addIssue({
          code: "custom",
          path: ["hourlyRate"],
          message: "Hourly rate must be greater than zero.",
        });
      }
    }

    if (item.billingType === "quantity") {
      if (item.quantity <= 0) {
        context.addIssue({
          code: "custom",
          path: ["quantity"],
          message: "Quantity must be greater than zero.",
        });
      }

      if (item.unitPrice <= 0) {
        context.addIssue({
          code: "custom",
          path: ["unitPrice"],
          message: "Unit price must be greater than zero.",
        });
      }
    }

    if (
      item.billingType === "fixed" &&
      item.fixedAmount <= 0
    ) {
      context.addIssue({
        code: "custom",
        path: ["fixedAmount"],
        message: "Fixed amount must be greater than zero.",
      });
    }
  });

export const invoiceFormSchema = z
  .object({
    clientId: z.string(),
    company: companyFormSchema,
    client: partyFormSchema,
    issueDate: z
      .string()
      .refine(isDateOnly, "Enter a valid invoice date."),
    serviceStartDate: z
      .string()
      .refine(
        (value) => value === "" || isDateOnly(value),
        "Enter a valid start date.",
      ),
    serviceEndDate: z
      .string()
      .refine(
        (value) => value === "" || isDateOnly(value),
        "Enter a valid end date.",
      ),
    billingCycle: z.enum(BILLING_CYCLES),
    currency: z.enum(CURRENCIES),
    taxRatePercent: z.number().min(0).max(100),
    dueKind: z.enum([
      "none",
      "upon_receipt",
      "specific",
      "net",
      "weekly",
      "every_n_weeks",
      "monthly",
      "custom",
    ]),
    dueSpecificDate: z.string(),
    dueNetDays: z.number().int().min(0).max(365),
    dueWeekday: z.enum(WEEKDAYS),
    dueIntervalWeeks: z.number().int().min(1).max(52),
    dueAnchorDate: z.string(),
    dueMonthlyDay: z.number().int().min(1).max(31),
    dueCustomLabel: z.string().trim().max(300),
    paymentTerms: z.string().trim().max(1000),
    paymentInstructions: z.string().trim().max(1000),
    paymentQr: z.object({
      gcashEnabled: z.boolean(),
      maribankEnabled: z.boolean(),
      preferred: z.enum(PAYMENT_QR_METHODS),
    }),
    publicNotes: z.string().trim().max(3000),
    internalNotes: z.string().trim().max(3000),
    items: z
      .array(invoiceFormItemSchema)
      .min(1, "Add at least one line item.")
      .max(100),
  })
  .superRefine((value, context) => {
    if (
      Boolean(value.serviceStartDate) !==
      Boolean(value.serviceEndDate)
    ) {
      context.addIssue({
        code: "custom",
        path: value.serviceStartDate
          ? ["serviceEndDate"]
          : ["serviceStartDate"],
        message:
          "Enter both service dates, or leave both blank.",
      });
    }

    if (
      value.serviceStartDate &&
      value.serviceEndDate &&
      value.serviceEndDate < value.serviceStartDate
    ) {
      context.addIssue({
        code: "custom",
        path: ["serviceEndDate"],
        message: "End date cannot be before the start date.",
      });
    }

    if (
      value.dueKind === "specific" &&
      !isDateOnly(value.dueSpecificDate)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueSpecificDate"],
        message: "Enter the agreed due date.",
      });
    }

    if (
      value.dueKind === "every_n_weeks" &&
      !isDateOnly(value.dueAnchorDate)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueAnchorDate"],
        message: "Enter the first agreed due date.",
      });
    }

    if (
      value.dueKind === "custom" &&
      value.dueCustomLabel.trim() === ""
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueCustomLabel"],
        message: "Describe the agreed payment terms.",
      });
    }

    const preferredQrEnabled =
      value.paymentQr.preferred === "gcash"
        ? value.paymentQr.gcashEnabled
        : value.paymentQr.maribankEnabled;

    if (
      (value.paymentQr.gcashEnabled ||
        value.paymentQr.maribankEnabled) &&
      !preferredQrEnabled
    ) {
      context.addIssue({
        code: "custom",
        path: ["paymentQr", "preferred"],
        message:
          "Choose an enabled QR payment method as preferred.",
      });
    }
  });

export type InvoiceFormValues = z.infer<
  typeof invoiceFormSchema
>;
export type InvoiceFormItem = z.infer<
  typeof invoiceFormItemSchema
>;

export function emptyAddress() {
  return {
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  };
}

export function emptyParty(): PartyInfo {
  return {
    name: "",
    company: "",
    email: "",
    phone: "",
    address: emptyAddress(),
  };
}

function defaultPresetForType(
  type: BillingItemType,
): RateUnitPreset {
  if (type === "hourly") {
    return "hour";
  }

  if (type === "fixed") {
    return "project";
  }

  return "item";
}

export function unitLabelToFormFields(
  unitLabel: string,
  billingType: BillingItemType,
): Pick<
  InvoiceFormItem,
  "unitPreset" | "customUnitLabel"
> {
  const normalized = unitLabel.trim().toLowerCase();
  const matchingPreset = RATE_UNIT_PRESETS.find(
    (preset) =>
      preset !== "custom" &&
      RATE_UNIT_LABELS[preset] === normalized,
  );

  if (matchingPreset) {
    return {
      unitPreset: matchingPreset,
      customUnitLabel: "",
    };
  }

  if (!normalized) {
    return {
      unitPreset: defaultPresetForType(billingType),
      customUnitLabel: "",
    };
  }

  return {
    unitPreset: "custom",
    customUnitLabel: unitLabel.trim(),
  };
}

export function resolvedUnitLabel(
  item: Pick<
    InvoiceFormItem,
    "billingType" | "unitPreset" | "customUnitLabel"
  >,
): string {
  if (item.billingType === "hourly") {
    return "hour";
  }

  if (item.unitPreset === "custom") {
    return item.customUnitLabel.trim();
  }

  return RATE_UNIT_LABELS[item.unitPreset];
}

export function newFormItem(
  type: InvoiceFormItem["billingType"] = "hourly",
  defaultRate = 0,
  defaultUnit = "",
): InvoiceFormItem {
  const unitFields = unitLabelToFormFields(
    defaultUnit,
    type,
  );

  return {
    id: createId("item"),
    description: "",
    billingType: type,
    duration: "1:00:00",
    hourlyRate: type === "hourly" ? defaultRate : 0,
    quantity: 1,
    ...unitFields,
    unitPrice: type === "quantity" ? defaultRate : 0,
    fixedAmount: type === "fixed" ? defaultRate : 0,
  };
}

function dueRuleToFields(
  rule: DueRule,
): Pick<
  InvoiceFormValues,
  | "dueKind"
  | "dueSpecificDate"
  | "dueNetDays"
  | "dueWeekday"
  | "dueIntervalWeeks"
  | "dueAnchorDate"
  | "dueMonthlyDay"
  | "dueCustomLabel"
> {
  return {
    dueKind: rule.kind,
    dueSpecificDate:
      rule.kind === "specific" ? rule.date : "",
    dueNetDays: rule.kind === "net" ? rule.days : 15,
    dueWeekday:
      rule.kind === "weekly" ? rule.weekday : "friday",
    dueIntervalWeeks:
      rule.kind === "every_n_weeks"
        ? rule.intervalWeeks
        : 2,
    dueAnchorDate:
      rule.kind === "every_n_weeks"
        ? rule.anchorDate
        : toDateOnly(),
    dueMonthlyDay:
      rule.kind === "monthly" ? rule.dayOfMonth : 15,
    dueCustomLabel:
      rule.kind === "custom" ? rule.label : "",
  };
}

export function createInvoiceFormDefaults(
  company: CompanyProfile,
  client?: ClientProfile,
  defaultCurrency: CurrencyCode = "USD",
  paymentQrAssets?: PaymentQrAssetMap,
): InvoiceFormValues {
  const due = dueRuleToFields(
    client?.defaults.dueRule ?? { kind: "none" },
  );

  const billingType =
    client?.defaults.billingType ??
    (client?.defaults.hourlyRateCents
      ? "hourly"
      : "fixed");

  const rate = centsToDecimal(
    client?.defaults.rateCents ??
      client?.defaults.hourlyRateCents ??
      0,
  );

  const rateUnit =
    client?.defaults.rateUnit ||
    (billingType === "hourly" ? "hour" : "project");

  return {
    clientId: client?.id ?? "",
    company: structuredClone(company),
    client: client
      ? {
          name: client.name,
          company: client.company,
          email: client.email,
          phone: client.phone,
          address: structuredClone(client.address),
        }
      : emptyParty(),
    issueDate: toDateOnly(),
    serviceStartDate: "",
    serviceEndDate: "",
    billingCycle:
      client?.defaults.billingCycle ?? "one_time",
    currency: client?.defaults.currency ?? defaultCurrency,
    taxRatePercent: 0,
    ...due,
    paymentTerms:
      client?.defaults.paymentTerms ?? "",
    paymentInstructions:
      client?.defaults.paymentInstructions ?? "",
    paymentQr: defaultPaymentQrSettings(
      client?.defaults.currency ?? defaultCurrency,
      paymentQrAssets,
    ),
    publicNotes: DEFAULT_CLIENT_NOTE,
    internalNotes: "",
    items: [newFormItem(billingType, rate, rateUnit)],
  };
}

export function invoiceToForm(
  invoice: InvoiceRecord,
): InvoiceFormValues {
  return {
    clientId: invoice.clientId ?? "",
    company: structuredClone(invoice.company),
    client: structuredClone(invoice.client),
    issueDate: invoice.issueDate,
    serviceStartDate: invoice.serviceStartDate,
    serviceEndDate: invoice.serviceEndDate,
    billingCycle: invoice.billingCycle,
    currency: invoice.currency,
    taxRatePercent: invoice.taxRateBps / 100,
    ...dueRuleToFields(invoice.dueRule),
    paymentTerms: invoice.paymentTerms,
    paymentInstructions: invoice.paymentInstructions,
    paymentQr: structuredClone(invoice.paymentQr),
    publicNotes: invoice.publicNotes,
    internalNotes: invoice.internalNotes,
    items: invoice.items.map((item) => ({
      id: item.id,
      description: item.description,
      billingType: item.billingType,
      duration: formatDuration(item.durationSeconds),
      hourlyRate: centsToDecimal(item.hourlyRateCents),
      quantity: milliToDecimal(item.quantityMilli),
      ...unitLabelToFormFields(
        item.unitLabel,
        item.billingType,
      ),
      unitPrice: centsToDecimal(item.unitPriceCents),
      fixedAmount: centsToDecimal(
        item.fixedAmountCents,
      ),
    })),
  };
}

function fieldsToDueRule(
  values: InvoiceFormValues,
): DueRule {
  switch (values.dueKind) {
    case "none":
      return { kind: "none" };

    case "upon_receipt":
      return { kind: "upon_receipt" };

    case "specific":
      return {
        kind: "specific",
        date: values.dueSpecificDate,
      };

    case "net":
      return { kind: "net", days: values.dueNetDays };

    case "weekly":
      return {
        kind: "weekly",
        weekday: values.dueWeekday,
      };

    case "every_n_weeks":
      return {
        kind: "every_n_weeks",
        intervalWeeks: values.dueIntervalWeeks,
        anchorDate: values.dueAnchorDate,
      };

    case "monthly":
      return {
        kind: "monthly",
        dayOfMonth: values.dueMonthlyDay,
      };

    case "custom":
      return {
        kind: "custom",
        label: values.dueCustomLabel,
      };

    default: {
      const exhaustive: never = values.dueKind;
      throw new Error(
        `Unsupported due rule: ${String(exhaustive)}`,
      );
    }
  }
}

export function formToInvoiceInput(
  values: InvoiceFormValues,
): InvoiceInput {
  return {
    clientId: values.clientId || null,
    company: structuredClone(values.company),
    client: structuredClone(values.client),
    issueDate: values.issueDate,
    serviceStartDate: values.serviceStartDate,
    serviceEndDate: values.serviceEndDate,
    dueRule: fieldsToDueRule(values),
    billingCycle: values.billingCycle,
    currency: values.currency,
    taxRateBps: Math.round(
      values.taxRatePercent * 100,
    ),
    paymentTerms: values.paymentTerms,
    paymentInstructions: values.paymentInstructions,
    paymentQr: structuredClone(values.paymentQr),
    publicNotes: values.publicNotes,
    internalNotes: values.internalNotes,
    items: values.items.map((item) => ({
      id: item.id,
      description: item.description,
      billingType: item.billingType,
      durationSeconds:
        item.billingType === "hourly"
          ? parseDuration(item.duration)
          : 0,
      hourlyRateCents:
        item.billingType === "hourly"
          ? decimalToCents(item.hourlyRate)
          : 0,
      quantityMilli:
        item.billingType === "quantity"
          ? decimalToMilli(item.quantity)
          : 0,
      unitLabel: resolvedUnitLabel(item),
      unitPriceCents:
        item.billingType === "quantity"
          ? decimalToCents(item.unitPrice)
          : 0,
      fixedAmountCents:
        item.billingType === "fixed"
          ? decimalToCents(item.fixedAmount)
          : 0,
    })),
  };
}
